/**
 * Language Context
 *
 * Provides language state and RTL support for public pages.
 * Handles URL-based language detection and document direction.
 */

import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type Language = 'en' | 'ar';

interface LanguageContextType {
  language: Language;
  isRTL: boolean;
  toggleLanguage: () => void;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

interface LanguageProviderProps {
  children: ReactNode;
  lang: Language;
}

export function LanguageProvider({ children, lang }: LanguageProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  const isRTL = lang === 'ar';

  // Set document direction and language (no localStorage write on mount)
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
    i18n.changeLanguage(lang);
  }, [lang, isRTL, i18n]);

  const setLanguage = (newLang: Language) => {
    // Persist preference only on explicit user action
    localStorage.setItem('preferredLanguage', newLang);

    const currentPath = location.pathname;

    if (newLang === 'ar') {
      // Going to Arabic: add /ar prefix
      const pathWithoutAr = currentPath.replace(/^\/ar/, '') || '/';
      navigate(`/ar${pathWithoutAr === '/' ? '' : pathWithoutAr}`);
    } else {
      // Going to English: remove /ar prefix
      const pathWithoutAr = currentPath.replace(/^\/ar/, '') || '/';
      navigate(pathWithoutAr);
    }
  };

  const toggleLanguage = () => {
    setLanguage(lang === 'en' ? 'ar' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language: lang, isRTL, toggleLanguage, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

/**
 * Hook to get localized paths that respect current language
 */
export function useLocalizedPath() {
  const { language } = useLanguage();
  return (path: string) => {
    if (language === 'ar') {
      return `/ar${path === '/' ? '' : path}`;
    }
    return path;
  };
}
